import { Button, Accordion } from '@zoom/components';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
  CheckboxFilter,
  CheckboxFilterAccordionType,
  CheckboxFilterTile,
  createCheckboxFilterTileSubtitle,
} from '../../../components/CheckboxFilterTile/CheckboxFilterTile';
import { Legend } from '../../../components/Form/Legend';
import { SidebarBody, SidebarHeader, useSidebar, Sidebar } from '../../../components/layout/Sidebar';
import { Queue, Skill } from '../../../graphql/components';
import { sortAgentsBy, SortOrder } from '../../../types';
import { recordBy } from '../../../utils/array';
import { customSort } from '../../../utils/sort';
import { Agent } from '../../Agents/types';
import {
  ADD_PERSONS_SUBMIT_BUTTON_TAG,
  ADD_PERSONS_CANCEL_BUTTON_TAG,
  SHOW_ACTIVE_FILTERS_BUTTON_TAG,
} from '../constants';
import { PersonsBulkAssign } from './PersonsBulkAssign';
import { PersonsSidebarDropdown } from './PersonsSidebarDropdown';

type AddPersonsSidebarProps = {
  agents: Agent[];
  queues: Record<string, Queue>;
  skills: Record<string, Skill>;
  selectAgents: (selectedAgents: string[]) => void;
};

export interface CheckboxAgent extends Agent {
  assigned: boolean;
}

export const createFilterItemRecordObj = (items: Record<string, Queue> | Record<string, Skill>) => {
  const filterRecordObj: Record<string, CheckboxFilter> = {};

  Object.keys(items).forEach((itemId) => {
    const { id, name } = items[itemId];
    filterRecordObj[itemId] = { id, name, assigned: false };
  });

  return filterRecordObj;
};

export const createAvailableCheckboxAgentsObj = (allAgents: Agent[], defaultState: Record<string, CheckboxAgent>) => {
  const availableAgentsObj: Record<string, CheckboxAgent> = { ...defaultState };

  allAgents.forEach((agent) => {
    availableAgentsObj[agent.id] = {
      ...agent,
      assigned: defaultState[agent.id] ? defaultState[agent.id].assigned : false,
    };
  });

  return availableAgentsObj;
};

export const AddPersonsSidebar: React.FC<AddPersonsSidebarProps> = ({ agents, queues, skills, selectAgents }) => {
  const { t } = useTranslation();
  const { closeSidebar } = useSidebar();

  // Available Persons state
  const [selectedAgents, setSelectedAgents] = useState<Record<string, CheckboxAgent>>(
    createAvailableCheckboxAgentsObj(agents, {})
  );

  // Filters states
  const [selectedQueues, setSelectedQueues] = useState<Record<string, CheckboxFilter>>(
    createFilterItemRecordObj(queues)
  );
  const [selectedSkills, setSelectedSkills] = useState<Record<string, CheckboxFilter>>(
    createFilterItemRecordObj(skills)
  );

  useEffect(() => {
    // Agents should be updated with new agent, when user removes him/her from scheduling while having opened sidebar
    setSelectedAgents((previous) => createAvailableCheckboxAgentsObj(agents, previous));
  }, [agents]);

  // Accordion open tiles state
  const [preOpenedItemIds, setPreOpenedItemIds] = useState<string[]>([]);

  // Creating arrays[] of assigned Ids
  const assignedAgentIds = Object.keys(selectedAgents).filter((curr) => selectedAgents[curr].assigned);
  const assignedQueueIds = Object.keys(selectedQueues).filter((curr) => selectedQueues[curr].assigned);
  const assignedSkillIds = Object.keys(selectedSkills).filter((curr) => selectedSkills[curr].assigned);

  const calcActiveFilters = () =>
    [!!assignedQueueIds.length, !!assignedSkillIds.length].filter((active) => active).length;

  const filters: CheckboxFilterAccordionType[] = [
    {
      dataTestIdPrefix: 'add-persons-sidebar-queues',
      id: '1',
      items: selectedQueues,
      name: t('queue.queues'),
      setItems: setSelectedQueues,
      subtitle: createCheckboxFilterTileSubtitle(t, selectedQueues, assignedQueueIds),
    },
    {
      dataTestIdPrefix: 'add-persons-sidebar-skills',
      id: '2',
      items: selectedSkills,
      name: t('skill.skills'),
      setItems: setSelectedSkills,
      subtitle: createCheckboxFilterTileSubtitle(t, selectedSkills, assignedSkillIds),
    },
  ];

  const handleAddPersons = () => {
    const agentsRecordObj = recordBy(agents, 'id');
    const assignedAgentIdsSorted = customSort(
      agentsRecordObj,
      sortAgentsBy.name,
      SortOrder.ASCENDING,
      assignedAgentIds
    );
    selectAgents(assignedAgentIdsSorted);

    const toastMessage = t('person.added', { count: assignedAgentIds.length });

    toast.success(toastMessage);
    closeSidebar();
  };

  const handleResetAllFilters = () => {
    setSelectedQueues(createFilterItemRecordObj(queues));
    setSelectedSkills(createFilterItemRecordObj(skills));

    setPreOpenedItemIds([]);
  };

  const handleToggleActiveFilters = () => {
    if (preOpenedItemIds.length) {
      setPreOpenedItemIds([]);
    } else {
      const activeFilterIds = filters
        .map(({ items, id }) => {
          if (Object.keys(items).find((item) => items[item].assigned)) {
            return id;
          }
          return undefined;
        })
        .filter((item) => item) as string[];

      setPreOpenedItemIds(activeFilterIds);
    }
  };

  const countOfActiveFilters = calcActiveFilters();

  return (
    <Sidebar>
      <SidebarHeader onClose={closeSidebar}>
        <Button
          data-test-id={ADD_PERSONS_SUBMIT_BUTTON_TAG}
          disabled={!assignedAgentIds.length}
          kind="secondary"
          onClick={handleAddPersons}
        >
          {t('form.apply')}
        </Button>
        <Button
          className="button-icon-left mr-auto ml-15"
          color="info"
          data-test-id={ADD_PERSONS_CANCEL_BUTTON_TAG}
          icon={{ color: 'red', kind: 'cross', size: 10 }}
          onClick={closeSidebar}
        >
          {t('layout.action.cancel')}
        </Button>
        <PersonsSidebarDropdown
          isFiltrationActive={!!countOfActiveFilters}
          resetAllFilters={handleResetAllFilters}
          t={t}
        />
      </SidebarHeader>
      <SidebarBody>
        <div className="persons-sidebar__filters-header-wrapper">
          <Legend>{t('filter.filters')}</Legend>
          <Button
            className={classnames('persons-sidebar__filters-info-wrapper', {
              'persons-sidebar__filters-info-wrapper--inactive': !countOfActiveFilters,
            })}
            data-test-id={SHOW_ACTIVE_FILTERS_BUTTON_TAG}
            disabled={!countOfActiveFilters}
            kind="link"
            onClick={handleToggleActiveFilters}
          >
            <Trans i18nKey="filter.activeFilters" values={{ count: countOfActiveFilters }}>
              {/* space needs to be added within strong tag so the tests can run correctly */}
              {/* @ts-expect-error: Value needs to be interpolated, using double bracelets */}
              <strong>{{ countOfActiveFilters }} </strong>Active filters
            </Trans>
          </Button>
        </div>
        <div className="persons-sidebar__filters">
          <Accordion
            allowMultipleOpened
            items={filters}
            label={({ name }: CheckboxFilterAccordionType) => name}
            preOpenedItemIds={preOpenedItemIds}
          >
            {(item) => <CheckboxFilterTile filter={item} />}
          </Accordion>
        </div>
        <div className="persons-sidebar__available-persons">
          <PersonsBulkAssign
            assignedQueueIds={assignedQueueIds}
            assignedSkillIds={assignedSkillIds}
            queues={queues}
            selectedAgents={selectedAgents}
            skills={skills}
            setSelectedAgents={setSelectedAgents}
          />
        </div>
      </SidebarBody>
    </Sidebar>
  );
};
